/**
 * Goal schemas for tracking health metrics against targets.
 *
 * Two goal types via discriminated union on `goal_type`:
 * - `metric`: windowed sum of a predefined metric (e.g., "70k steps per week")
 * - `trend`: EMA trend value against a target (e.g., "keep trend below 0.7/month")
 */
import { z } from 'zod';
import { baseResponseSchema, metricTypeSchema } from "./common.js";
import { trendDisplayPeriodSchema, trendSourceTypeSchema } from "./trends.js";
/**
 * Duration string for goal window (e.g., '7d', '2w', '1M').
 * Units: s=seconds, m=minutes, h=hours, d=days, w=weeks, M=months
 */
export const durationStringSchema = z
    .string()
    .regex(/^\d+[smhdwM]$/, 'Duration must be a number followed by a unit (s, m, h, d, w, M)')
    .meta({
    description: 'Duration string (e.g., "7d", "2w", "1M")',
    example: '7d',
    id: 'DurationString',
});
/**
 * Parse duration string to milliseconds and components.
 */
export const parseDuration = (duration) => {
    const match = duration.match(/^(\d+)([smhdwM])$/);
    if (!match)
        throw new Error(`Invalid duration: ${duration}`);
    const value = parseInt(match[1], 10);
    const unit = match[2];
    const multipliers = {
        M: 30 * 24 * 60 * 60 * 1000, // Approximate month
        d: 24 * 60 * 60 * 1000,
        h: 60 * 60 * 1000,
        m: 60 * 1000,
        s: 1000,
        w: 7 * 24 * 60 * 60 * 1000,
    };
    return { ms: value * multipliers[unit], unit, value };
};
/**
 * Check if a duration unit is calendar-based (days, weeks, months).
 * Calendar-based windows align to day boundaries.
 * Rolling windows (hours, minutes, seconds) use exact time offsets.
 */
export const isCalendarBasedUnit = (unit) => unit === 'd' || unit === 'w' || unit === 'M';
// ── Metric goal (windowed sum) ───────────────────────────────────────────────
/**
 * Metric goal: track a predefined metric's sum within a rolling window.
 */
export const metricGoalSchema = z
    .object({
    goal_type: z.literal('metric').default('metric').meta({ description: 'Goal type discriminant' }),
    id: z.string().uuid().meta({ description: 'Unique identifier for the goal' }),
    max: z.number().positive().optional().meta({ description: 'Maximum target value' }),
    metric: metricTypeSchema.meta({ description: 'Metric to track' }),
    min: z.number().positive().optional().meta({ description: 'Minimum target value' }),
    window: durationStringSchema.default('7d').meta({ description: 'Rolling window duration' }),
})
    .refine((data) => data.min !== undefined || data.max !== undefined, {
    message: 'At least one of min or max must be specified',
})
    .refine((data) => data.min === undefined || data.max === undefined || data.min <= data.max, {
    message: 'Min must be less than or equal to max',
})
    .meta({ description: 'Metric goal: windowed sum of a predefined metric', id: 'MetricGoal' });
// ── Trend goal (EMA target) ─────────────────────────────────────────────────
/**
 * Trend goal: track an EMA trend value against a min/max target.
 */
export const trendGoalSchema = z
    .object({
    aggregation: z
        .enum(['count', 'sum', 'mean'])
        .default('count')
        .meta({ description: 'How to aggregate source data' }),
    display_period: trendDisplayPeriodSchema
        .default('monthly')
        .meta({ description: 'Display period for the trend value' }),
    goal_type: z.literal('trend').meta({ description: 'Goal type discriminant' }),
    half_life_days: z.number().positive().default(15).meta({ description: 'EMA half-life in days' }),
    id: z.string().uuid().meta({ description: 'Unique identifier for the goal' }),
    max: z.number().optional().meta({ description: 'Maximum target value' }),
    min: z.number().optional().meta({ description: 'Minimum target value' }),
    pattern: z.string().meta({ description: 'Activity type name, metric name, or category path' }),
    source_type: trendSourceTypeSchema.meta({ description: 'Type of data source for the trend' }),
})
    .refine((data) => data.min !== undefined || data.max !== undefined, {
    message: 'At least one of min or max must be specified',
})
    .refine((data) => data.min === undefined || data.max === undefined || data.min <= data.max, {
    message: 'Min must be less than or equal to max',
})
    .meta({ description: 'Trend goal: EMA trend value against a target', id: 'TrendGoal' });
// ── Union ────────────────────────────────────────────────────────────────────
/**
 * Goal schema - discriminated union of metric and trend goals.
 */
export const goalSchema = z.discriminatedUnion('goal_type', [metricGoalSchema, trendGoalSchema]).meta({
    id: 'Goal',
});
/**
 * Goals array schema.
 */
export const goalsSchema = z.array(goalSchema).meta({
    description: 'List of goals',
    id: 'Goals',
});
/**
 * Default goals based on Huberman/Galpin recommendations.
 */
export const defaultGoals = [
    {
        goal_type: 'metric',
        id: 'a0000001-0000-4000-8000-000000000001',
        metric: 'hr_zone_2_sec',
        min: 9000, // 150 minutes in seconds
        window: '7d',
    },
    {
        goal_type: 'metric',
        id: 'a0000002-0000-4000-8000-000000000002',
        max: 600, // 10 minutes in seconds
        metric: 'hr_zone_5_sec',
        min: 300, // 5 minutes in seconds
        window: '7d',
    },
    {
        goal_type: 'metric',
        id: 'a0000003-0000-4000-8000-000000000003',
        metric: 'steps',
        min: 70000,
        window: '7d',
    },
];
// ── Progress schemas ─────────────────────────────────────────────────────────
/**
 * Metric goal progress - includes current windowed sum and losing-tomorrow.
 */
export const metricGoalProgressSchema = z
    .object({
    current: z.number().meta({ description: 'Current value within the window' }),
    goal_type: z.literal('metric').default('metric').meta({ description: 'Goal type discriminant' }),
    id: z.string().uuid().meta({ description: 'Goal ID' }),
    losing_tomorrow: z
        .number()
        .meta({ description: 'Value that will drop off when oldest period exits window' }),
    max: z.number().positive().optional(),
    metric: metricTypeSchema,
    min: z.number().positive().optional(),
    unit: z.string().meta({ description: 'Storage unit for the metric' }),
    window: durationStringSchema,
})
    .meta({ description: 'Progress for a metric goal', id: 'MetricGoalProgress' });
/**
 * Trend goal progress - current EMA value compared to target.
 */
export const trendGoalProgressSchema = z
    .object({
    current: z.number().meta({ description: 'Current EMA trend value' }),
    display_period: trendDisplayPeriodSchema.meta({ description: 'Display period for the value' }),
    display_unit: z.string().meta({ description: 'Human-readable unit (e.g., "per month")' }),
    goal_type: z.literal('trend').meta({ description: 'Goal type discriminant' }),
    id: z.string().uuid().meta({ description: 'Goal ID' }),
    max: z.number().optional(),
    min: z.number().optional(),
    pattern: z.string().meta({ description: 'Source pattern name' }),
    source_type: trendSourceTypeSchema,
})
    .meta({ description: 'Progress for a trend goal', id: 'TrendGoalProgress' });
/**
 * Goal progress - discriminated union of metric and trend progress.
 */
export const goalProgressSchema = z
    .discriminatedUnion('goal_type', [metricGoalProgressSchema, trendGoalProgressSchema])
    .meta({ id: 'GoalProgress' });
/**
 * Goals progress response schema.
 */
export const goalsProgressResponseSchema = baseResponseSchema
    .extend({
    goals: z.array(goalProgressSchema).meta({ description: 'Progress for all goals' }),
})
    .meta({ id: 'GoalsProgressResponse' });
// ── Widget-friendly flat progress ────────────────────────────────────────────
/**
 * Simplified goal progress for widgets (Android, etc.).
 * Flat structure — no discriminated union, works with simple Kotlin data classes.
 */
export const widgetGoalProgressSchema = z
    .object({
    current: z.number().meta({ description: 'Current progress value' }),
    id: z.string().uuid().meta({ description: 'Goal ID' }),
    losing_tomorrow: z.number().meta({ description: 'Value dropping off tomorrow (0 for trend goals)' }),
    max: z.number().optional(),
    min: z.number().optional(),
    title: z.string().meta({ description: 'Display title for the goal' }),
    unit: z.string().meta({ description: 'Display unit (e.g., "min", "count", "per month")' }),
})
    .meta({ description: 'Flat goal progress for widgets', id: 'WidgetGoalProgress' });
export const widgetGoalsProgressResponseSchema = baseResponseSchema
    .extend({
    goals: z.array(widgetGoalProgressSchema).meta({ description: 'Flat progress for all goals' }),
})
    .meta({ id: 'WidgetGoalsProgressResponse' });
//# sourceMappingURL=goals.js.map